import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { 
  QrCode, Plus, Edit, Trash2, Download, Eye, 
  Table as TableIcon, Grid3X3
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { cn } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TablesManager = () => {
  const [tables, setTables] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [formData, setFormData] = useState({
    table_number: "",
    capacity: 4,
    is_active: true,
    qr_type: "restaurant_table",
    qr_name: "",
    floor: "",
    branch: "",
    zone: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resResponse, tablesResponse] = await Promise.all([
        axios.get(`${API}/restaurant/profile`),
        axios.get(`${API}/restaurant/tables`),
      ]);
      setRestaurant(resResponse.data);
      setTables(tablesResponse.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingTable) {
        await axios.put(`${API}/restaurant/tables/${editingTable.id}`, formData);
        toast.success("Table updated!");
      } else {
        await axios.post(`${API}/restaurant/tables`, formData);
        toast.success("Table created!");
      }
      fetchData();
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save table");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this table?")) return;
    try {
      await axios.delete(`${API}/restaurant/tables/${id}`);
      toast.success("Table deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete table");
    }
  };

  const handleToggleActive = async (table) => {
    try {
      await axios.put(`${API}/restaurant/tables/${table.id}`, { is_active: !table.is_active });
      fetchData();
    } catch (error) {
      toast.error("Failed to update table");
    }
  };

  const openEditDialog = (table) => {
    setEditingTable(table);
    setFormData({
      table_number: table.table_number,
      capacity: table.capacity,
      is_active: table.is_active,
      qr_type: table.qr_type || "restaurant_table",
      qr_name: table.qr_name || "",
      floor: table.floor || "",
      branch: table.branch || "",
      zone: table.zone || "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingTable(null);
    setFormData({ table_number: "", capacity: 4, is_active: true, qr_type: "restaurant_table", qr_name: "", floor: "", branch: "", zone: "" });
  };

  const openQRDialog = (table) => {
    setSelectedTable(table);
    setQrDialogOpen(true);
  };

  const downloadQR = (tableNumber) => {
    // Try both ids — list view uses `qr-{n}`, dialog uses `qr-dialog-{n}`.
    const svg =
      document.getElementById(`qr-dialog-${tableNumber}`) ||
      document.getElementById(`qr-${tableNumber}`);
    if (!svg) {
      toast.error("QR code not ready — please try again");
      return;
    }
    try {
      const svgData = new XMLSerializer().serializeToString(svg);
      // Use Blob URL instead of btoa() so Unicode characters in the restaurant
      // name (Somali, emoji, etc.) don't break the encoding.
      const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      const triggerDownload = (href, filename) => {
        // Use a real click event so mobile Safari/Chrome allow the download.
        // Also try opening in new tab as a fallback for browsers that block
        // programmatic anchor clicks.
        const link = document.createElement("a");
        link.download = filename;
        link.href = href;
        link.rel = "noopener";
        link.target = "_blank";
        document.body.appendChild(link);
        try {
          link.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
        } catch (_) {
          link.click();
        }
        setTimeout(() => {
          try { document.body.removeChild(link); } catch (_) {}
        }, 200);
      };

      img.onload = () => {
        canvas.width = 400;
        canvas.height = 500;
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 50, 50, 300, 300);

        ctx.fillStyle = "black";
        ctx.font = "bold 24px Arial";
        ctx.textAlign = "center";
        ctx.fillText(restaurant?.name || "Restaurant", 200, 400);
        ctx.font = "20px Arial";
        ctx.fillText(`Table ${tableNumber}`, 200, 440);
        ctx.font = "14px Arial";
        ctx.fillText("Scan to Order", 200, 475);

        // Try blob download first (works on most browsers including mobile),
        // fall back to dataURL if canvas.toBlob is unsupported.
        const filename = `table-${tableNumber}-qr.png`;
        if (canvas.toBlob) {
          canvas.toBlob((pngBlob) => {
            if (pngBlob) {
              const dlUrl = URL.createObjectURL(pngBlob);
              triggerDownload(dlUrl, filename);
              toast.success(`Downloading Table ${tableNumber} QR…`);
              setTimeout(() => { URL.revokeObjectURL(dlUrl); URL.revokeObjectURL(url); }, 10000);
            } else {
              // Fallback to dataURL
              const dataUrl = canvas.toDataURL("image/png");
              triggerDownload(dataUrl, filename);
              toast.success(`Downloading Table ${tableNumber} QR…`);
              URL.revokeObjectURL(url);
            }
          }, "image/png");
        } else {
          const dataUrl = canvas.toDataURL("image/png");
          triggerDownload(dataUrl, filename);
          toast.success(`Downloading Table ${tableNumber} QR…`);
          URL.revokeObjectURL(url);
        }
      };

      img.onerror = () => {
        toast.error("Couldn't render QR for download");
        URL.revokeObjectURL(url);
      };

      img.crossOrigin = "anonymous";
      img.src = url;
    } catch (e) {
      console.error("QR download error:", e);
      toast.error("QR download failed: " + (e?.message || "unknown"));
    }
  };

  const getOrderUrl = (tableNumber) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/r/${restaurant?.slug}?table=${tableNumber}`;
  };

  const generateBulkTables = async () => {
    const count = prompt("How many tables to generate?", "10");
    if (!count) return;
    
    const num = parseInt(count);
    if (isNaN(num) || num < 1 || num > 50) {
      toast.error("Please enter a number between 1 and 50");
      return;
    }
    
    try {
      await axios.post(`${API}/restaurant/tables/bulk`, { count: num });
      toast.success(`${num} tables created!`);
      fetchData();
    } catch (error) {
      toast.error("Failed to create tables");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="tables-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Grid3X3 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">QR & Tables</h1>
              <p className="text-sm text-muted-foreground">
                Manage tables and generate QR codes
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={generateBulkTables} data-testid="bulk-create-btn">
              <Grid3X3 className="w-4 h-4 mr-2" />
              Generate Multiple
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-table-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Table
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingTable ? "Edit Table" : "Add New Table"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Table Number *</Label>
                    <Input
                      value={formData.table_number}
                      onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
                      placeholder="e.g., 1, A1, Patio-1"
                      data-testid="table-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity</Label>
                    <Input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 4 })}
                      min={1}
                      max={20}
                      data-testid="table-capacity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>QR Type</Label>
                    <select className="w-full border rounded-md p-2 text-sm"
                      value={formData.qr_type}
                      onChange={(e) => setFormData({ ...formData, qr_type: e.target.value })}
                      data-testid="qr-type-select">
                      <option value="restaurant_table">Restaurant Table</option>
                      <option value="cafe_table">Cafe Table</option>
                      <option value="hotel_room">Hotel Room</option>
                      <option value="vip_room">VIP Room</option>
                      <option value="outdoor_table">Outdoor Table</option>
                      <option value="takeaway_counter">Takeaway Counter</option>
                      <option value="food_court_table">Food Court Table</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>QR Name (optional)</Label>
                    <Input value={formData.qr_name} onChange={(e) => setFormData({ ...formData, qr_name: e.target.value })} placeholder="e.g., Terrace T5, Room 402" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2"><Label className="text-xs">Floor</Label><Input value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: e.target.value })} placeholder="Ground" /></div>
                    <div className="space-y-2"><Label className="text-xs">Branch</Label><Input value={formData.branch} onChange={(e) => setFormData({ ...formData, branch: e.target.value })} placeholder="Main" /></div>
                    <div className="space-y-2"><Label className="text-xs">Zone</Label><Input value={formData.zone} onChange={(e) => setFormData({ ...formData, zone: e.target.value })} placeholder="Garden" /></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Active</Label>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                  </div>
                  <Button onClick={handleSave} className="w-full" data-testid="save-table-btn">
                    {editingTable ? "Update Table" : "Create Table"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tables Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tables.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <TableIcon className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold text-lg mb-1">No tables yet</h3>
              <p className="text-muted-foreground text-sm mb-4">Add tables to generate QR codes for ordering</p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Table
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {tables.map((table) => (
              <Card key={table.id} className={cn("hover:shadow-md transition-shadow", !table.is_active && "opacity-60")}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <span className="font-bold text-primary">{table.table_number}</span>
                      </div>
                      <div>
                        <p className="font-semibold">{table.qr_name || `Table ${table.table_number}`}</p>
                        <p className="text-xs text-muted-foreground">
                          {table.capacity} seats
                          {table.qr_type && table.qr_type !== "restaurant_table" && <span> · {table.qr_type.replace(/_/g, " ")}</span>}
                          {table.floor && <span> · {table.floor}</span>}
                          {table.zone && <span> · {table.zone}</span>}
                        </p>
                      </div>
                    </div>
                    <Badge variant={table.is_active ? "default" : "secondary"}>
                      {table.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  
                  {/* QR Preview */}
                  <div 
                    className="bg-white p-3 rounded-lg mb-3 flex items-center justify-center cursor-pointer hover:bg-gray-50"
                    onClick={() => openQRDialog(table)}
                  >
                    <QRCodeSVG
                      id={`qr-${table.table_number}`}
                      value={getOrderUrl(table.table_number)}
                      size={120}
                      level="M"
                    />
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => openQRDialog(table)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => downloadQR(table.table_number)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(table)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(table.id)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* QR Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>QR Code - Table {selectedTable?.table_number}</DialogTitle>
            </DialogHeader>
            {selectedTable && (
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl flex flex-col items-center">
                  <QRCodeSVG
                    id={`qr-dialog-${selectedTable.table_number}`}
                    value={getOrderUrl(selectedTable.table_number)}
                    size={200}
                    level="H"
                    includeMargin
                  />
                  <h3 className="font-bold text-lg mt-4">{restaurant?.name}</h3>
                  <p className="text-muted-foreground">Table {selectedTable.table_number}</p>
                  <p className="text-sm text-muted-foreground">Scan to Order</p>
                </div>
                <div className="text-center text-xs text-muted-foreground">
                  <p className="break-all">{getOrderUrl(selectedTable.table_number)}</p>
                </div>
                <Button 
                  className="w-full"
                  onClick={() => downloadQR(selectedTable.table_number)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download QR Code
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default TablesManager;
